import { FC, ReactNode, useCallback } from 'react'

import { useAppSelector } from '../../../../hooks/storeHooks'
import { useRouteModal } from '../../../route-modals/hooks/useRouteModal'
import { EModalType } from '../../../route-modals/types/modalTypes'
import { EventDialogTab } from '../../hooks/useEventDialogTabs'
import { useTrackedGames } from '../../hooks/useTrackedGames'
import { useDefaultLiveEvents } from '../../hooks/useTrackedGamesEvents'
import { selectHighlightedEvent } from '../../slices/liveEventModalSlice'
import { TrackedGame } from '../../types/TrackedGame'
import { TrackingEvent } from '../../types/TrackingEvents'
import { LiveEventsEventDialog } from './LiveEventsEventDialog'

export type LiveEventsEventDialogParams = {
  liveEventTypeId: string
  liveEventId: string
  trackedGameId: string
  durationStart?: string
  durationEnd?: string
  tab: EventDialogTab
  screenshotIndex: number
}

type LiveEventsEventDialogContainerProps = LiveEventsEventDialogParams & {
  children?: ReactNode
}

const initialScreenshotIndex = 0

export const LiveEventsEventDialogContainer: FC<LiveEventsEventDialogContainerProps> = ({
  liveEventTypeId: eventTypeId,
  liveEventId: eventId,
  trackedGameId,
  durationStart,
  durationEnd,
  tab = EventDialogTab.AllEvents,
  screenshotIndex = initialScreenshotIndex,
  children,
}) => {
  const { popModal, replaceModal } = useRouteModal()
  const trackedGames = useTrackedGames({})
  const trackedEvents = useDefaultLiveEvents({ trackedGameIds: [trackedGameId] })
  const trackedGame = trackedGames.data?.find((trackedGame) => trackedGame.game.id === trackedGameId)
  const highlightedEventId = useAppSelector(selectHighlightedEvent)
  const resolvedDurationStart = durationStart !== undefined && Number.isFinite(+durationStart) ? +durationStart : undefined
  const resolvedDurationEnd = durationEnd !== undefined && Number.isFinite(+durationEnd) ? +durationEnd : undefined

  const handleTabChange = useCallback(
    (tab: EventDialogTab) => {
      replaceModal({
        type: EModalType.LIVE_EVENT,
        params: {
          trackedGameId: trackedGameId,
          liveEventTypeId: eventTypeId,
          liveEventId: eventId,
          durationStart: resolvedDurationStart,
          durationEnd: resolvedDurationEnd,
          tab: tab,
          screenshotIndex: +screenshotIndex,
        },
      })
    },
    [replaceModal, trackedGameId, eventTypeId, eventId, resolvedDurationStart, resolvedDurationEnd, screenshotIndex]
  )

  const handleEventChange = useCallback(
    (event: TrackingEvent) => {
      replaceModal({
        type: EModalType.LIVE_EVENT,
        params: {
          trackedGameId: trackedGameId,
          liveEventTypeId: event.typeId,
          liveEventId: event.eventId,
          durationStart: event.start,
          durationEnd: event.end,
          tab: EventDialogTab.Description,
          screenshotIndex: initialScreenshotIndex,
        },
      })
    },
    [replaceModal, trackedGameId]
  )

  const handleTrackedGameChange = useCallback(
    (trackedGame: TrackedGame) => {
      replaceModal({
        type: EModalType.LIVE_EVENT,
        params: {
          trackedGameId: trackedGame.game.id,
          liveEventTypeId: eventTypeId,
          tab: EventDialogTab.AllEvents,
          screenshotIndex: initialScreenshotIndex,
        },
      })
    },
    [eventTypeId, replaceModal]
  )

  const handleScreenshotIndexChange = useCallback(
    (index: number) => {
      replaceModal({
        type: EModalType.LIVE_EVENT,
        params: {
          trackedGameId,
          liveEventTypeId: eventTypeId,
          liveEventId: eventId,
          durationStart: resolvedDurationStart,
          durationEnd: resolvedDurationEnd,
          tab,
          screenshotIndex: index,
        },
      })
    },
    [replaceModal, trackedGameId, eventTypeId, eventId, resolvedDurationStart, resolvedDurationEnd, tab]
  )

  const handleClose = () => {
    popModal()
  }

  return (
    <LiveEventsEventDialog
      data={{
        eventId: eventId,
        eventTypeId: eventTypeId,
        trackedGame: trackedGame,
        tab: tab,
        highlightedEventId: highlightedEventId,
        durationStart: resolvedDurationStart,
        durationEnd: resolvedDurationEnd,
        screenshotIndex: +screenshotIndex,
        trackedEvents: trackedEvents.events,
        trackedGames: trackedGames.data,
      }}
      open
      onClose={handleClose}
      onEventChange={handleEventChange}
      onTabChange={handleTabChange}
      onTrackedGameChange={handleTrackedGameChange}
      onScreenshotIndexChange={handleScreenshotIndexChange}
    />
  )
}
